import React, { useState } from "react";
import { Controller, useFormContext, type Control } from "react-hook-form";
import "quill-mention/autoregister";
import "react-quill-new/dist/quill.snow.css";
import Select from "react-select";
import type CreateEmailMessageViewModel from "../../types/viewModels/CreateEmailMessageViewModel";
import { useGetEmails } from "../../hooks/queries/useSearch";
import { useDebounce } from "../../hooks/helpers/useDebounce";

type RecipientFieldName = "ToRecipients" | "CcRecipients" | "BccRecipients";

interface EmailRecipientInputProps {
  name: RecipientFieldName;
  label: string;
  control: Control<CreateEmailMessageViewModel>;
  searchValue: string;
  setSearchValue: (v: string) => void;
  query: ReturnType<typeof useGetEmails>;
  error?: string;
  children?: React.ReactNode;
}

const EmailRecipientInput: React.FC<EmailRecipientInputProps> = ({
  name,
  label,
  control,
  searchValue,
  setSearchValue,
  query,
  error,
  children,
}) => {
  return (
    <div className="flex items-center border-b border-gray-200 pl-2 pr-2 pt-1 pb-1">
      <div className="w-8 text-sm text-gray-500">{label}</div>

      <div className="flex-1">
        <Controller
          name={name}
          control={control}
          rules={{
            validate: (v = []) =>
              v?.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.Email)) ||
              "Invalid email address",
          }}
          render={({ field }) => (
            <Select
              isMulti
              value={field.value ?? []}
              onChange={(val) => field.onChange(val)}
              onBlur={field.onBlur}
              inputValue={searchValue}
              onInputChange={setSearchValue}
              options={query.data || []}
              getOptionLabel={(e) => e.Email}
              getOptionValue={(e) => e.Email}
              isLoading={query.isPending}
              placeholder=""
              menuPortalTarget={document.body}
              menuPosition="fixed"
              components={{
                IndicatorSeparator: () => null,
                IndicatorsContainer: () => null,
              }}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  border: "none",
                  minHeight: "auto",
                  boxShadow: "none",
                  fontSize: "12px",
                  cursor: "text",
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "12px",
                  width: "250px",
                  borderRadius: "2px",
                }),
                menuList: (baseStyles) => ({
                  ...baseStyles,
                  maxHeight: "250px",
                }),
                menuPortal: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 9999,
                }),
                multiValue: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "14px",
                  backgroundColor: "#eee",
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: "100%",
                  cursor: "text",
                }),
                multiValueRemove: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
              }}
            />
          )}
        />

        {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
      </div>

      {children}
    </div>
  );
};

const EmailReply: React.FC = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CreateEmailMessageViewModel>();

  const [toSearch, setToSearch] = useState<string>("");
  const [ccSearch, setCcSearch] = useState<string>("");
  const [bccSearch, setBccSearch] = useState<string>("");

  const debouncedTo = useDebounce(toSearch, 300);
  const debouncedCc = useDebounce(ccSearch, 300);
  const debouncedBcc = useDebounce(bccSearch, 300);

  const toQuery = useGetEmails(debouncedTo);
  const ccQuery = useGetEmails(debouncedCc);
  const bccQuery = useGetEmails(debouncedBcc);

  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  return (
    <div className="text-gray-600">
      <div className="mb-0">
        <EmailRecipientInput
          name="ToRecipients"
          label="To"
          control={control}
          searchValue={toSearch}
          setSearchValue={setToSearch}
          query={toQuery}
          error={errors.ToRecipients?.message}
        >
          <div className="flex space-x-4 mt-1">
            <div
              onClick={() => setShowCC(!showCC)}
              className={`p-1 text-sm text-gray-400 cursor-pointer hover:bg-gray-200 rounded color-gray-200 ${
                showCC ? "bg-sky-100" : ""
              }`}
            >
              Cc
            </div>

            <div
              onClick={() => setShowBCC(!showBCC)}
              className={`p-1 text-sm text-gray-400 cursor-pointer hover:bg-gray-200 rounded color-gray-200 ${
                showBCC ? "bg-sky-100" : ""
              }`}
            >
              Bcc
            </div>
          </div>
        </EmailRecipientInput>

        {showCC && (
          <EmailRecipientInput
            name="CcRecipients"
            label="Cc"
            control={control}
            searchValue={ccSearch}
            setSearchValue={setCcSearch}
            query={ccQuery}
            error={errors.CcRecipients?.message}
          />
        )}

        {showBCC && (
          <EmailRecipientInput
            name="BccRecipients"
            label="Bcc"
            control={control}
            searchValue={bccSearch}
            setSearchValue={setBccSearch}
            query={bccQuery}
            error={errors.BccRecipients?.message}
          />
        )}
      </div>
    </div>
  );
};

export default EmailReply;
